import { CONTROL_FRAMEWORK_MATRIX_HEAT_COLORS } from "src/modules/data-protection/constants/Common";

export const getControlFrameworkMatrixHeatLevel = (percent) => {
    const value = Number(percent || 0);

    if (value >= 70) {
        return "high";
    }
    if (value >= 50) {
        return "medium-high";
    }
    if (value >= 35) {
        return "medium";
    }
    return "low";
};

export const getControlFrameworkMatrixHeatColor = (percent) => {
    const level = getControlFrameworkMatrixHeatLevel(percent);
    return CONTROL_FRAMEWORK_MATRIX_HEAT_COLORS[
        level === "medium-high" ? "mediumHigh" : level
    ];
};

export const getDashboardMetric = (metric) => {
    if (metric != null && typeof metric === "object" && "amount" in metric) {
        return {
            amount: metric.amount ?? 0,
            percent: Number(metric.percent ?? 0),
        };
    }

    return {
        amount: metric ?? 0,
        percent: 0,
    };
};

export const formatControlNumber = (controlNumber, framework) => {
    if (controlNumber === undefined || controlNumber === null || controlNumber === "") {
        return "-";
    }

    const number = String(controlNumber);

    if (framework === "ISO27001") {
        return number.startsWith("A") ? number : `A${number}`;
    }

    if (framework === "ISO42001") {
        if (number.startsWith("A.")) {
            return number;
        }
        if (number.startsWith("A")) {
            return `A.${number.slice(1)}`;
        }
        return `A.${number}`;
    }

    return number;
};

export const isTisaxFramework = (framework) =>
    String(framework || "").toUpperCase() === "TISAX";

const FRAMEWORK_MATRIX_SORT_ORDER = ["ISO27001", "ISO42001", "C5_2020", "DORA", "NIS2", "TISAX"];

export const sortFrameworkMatrixKeys = (frameworks) =>
    [...frameworks].sort((a, b) => {
        const indexA = FRAMEWORK_MATRIX_SORT_ORDER.indexOf(a);
        const indexB = FRAMEWORK_MATRIX_SORT_ORDER.indexOf(b);

        if (indexA === -1 && indexB === -1) {
            return a.localeCompare(b);
        }
        if (indexA === -1) {
            return 1;
        }
        if (indexB === -1) {
            return -1;
        }
        return indexA - indexB;
    });

export const resolveFrameworkMatrixTarget = (dashboardData, matrixRows = []) => {
    const explicitTarget =
        dashboardData?.mapping_target_framework ??
        dashboardData?.filters?.mapping_target_framework ??
        dashboardData?.framework_matrix_target;

    if (explicitTarget) {
        return explicitTarget;
    }

    const targetCounts = matrixRows.reduce((counts, row) => {
        if (!row?.target_framework) {
            return counts;
        }

        counts[row.target_framework] = (counts[row.target_framework] || 0) + 1;
        return counts;
    }, {});

    const rankedTargets = Object.entries(targetCounts).sort(([, a], [, b]) => b - a);

    if (rankedTargets.length) {
        return rankedTargets[0][0];
    }

    return dashboardData?.filters?.framework ?? null;
};

export const resolveFrameworkMatrixSources = (
    dashboardData,
    matrixRows = [],
    targetFramework
) => {
    if (!targetFramework) {
        return [];
    }

    const fromCards = (dashboardData?.framework_cards || [])
        .filter((card) => card.enabled !== false && card.framework)
        .map((card) => card.framework);

    const fromMatrix = matrixRows
        .filter(
            (row) =>
                row.target_framework === targetFramework &&
                row.source_framework &&
                row.source_framework !== targetFramework
        )
        .map((row) => row.source_framework);

    const baseline = FRAMEWORK_MATRIX_SORT_ORDER.filter(
        (framework) => framework !== targetFramework
    );

    const combined = [...new Set([...baseline, ...fromCards, ...fromMatrix])].filter(
        (framework) => framework !== targetFramework
    );

    return sortFrameworkMatrixKeys(combined);
};

export const formatLegalEntityIdsParam = (legalEntityIds) => {
    if (!legalEntityIds || legalEntityIds === "all") {
        return undefined;
    }

    if (Array.isArray(legalEntityIds)) {
        if (!legalEntityIds.length) {
            return undefined;
        }

        return legalEntityIds;
    }

    if (typeof legalEntityIds === "string") {
        const ids = legalEntityIds
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);

        return ids.length ? ids : undefined;
    }

    return [legalEntityIds];
};

export const serializeControlRequestParams = (params = {}) => {
    const parts = [];

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
            return;
        }

        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (item !== undefined && item !== null && item !== "") {
                    parts.push(
                        `${encodeURIComponent(key)}[]=${encodeURIComponent(item)}`
                    );
                }
            });
            return;
        }

        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    });

    return parts.join("&");
};

export const getPolicyStatus = (control) => {
    if (control.policy_status) {
        return String(control.policy_status).toLowerCase();
    }

    const documents = control.documents;

    if (Array.isArray(documents)) {
        return documents.length ? "attached" : "missing";
    }

    if (control.has_documents != null) {
        return control.has_documents ? "attached" : "missing";
    }

    return Number(control.mappings_count || 0) > 0 ? "attached" : "missing";
};

export const getRiskLevelKey = (control) => {
    const level =
        control.risk_level ??
        control.highest_risk_level ??
        control.risks?.[0]?.rating ??
        control.risks?.[0]?.risk_level;

    return level ? String(level).toLowerCase() : null;
};
